<script>
export default {
  name: 'HistoryItem',
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  computed: {
    groupedItems() {
      const { items } = this.item;
      const products = new Map();

      items.forEach((detail) => {
        const product = products.get(detail.product_id) || {
          product_id: detail.product_id,
          product_name: detail.product_name,
          sku: detail.sku,
          stores: new Map(),
        };
        const delta = Number(detail.new_quantity || 0) - Number(detail.old_quantity || 0);
        const storeName = delta < 0
          ? detail.previous_store_name || detail.new_store_name || 'N/A'
          : detail.new_store_name || detail.previous_store_name || 'N/A';

        product.stores.set(
          storeName,
          (product.stores.get(storeName) || 0) + delta,
        );
        products.set(detail.product_id, product);
      });

      return Array.from(products.values()).reduce((groupedItems, product) => {
        const origins = [];
        const destinations = [];

        product.stores.forEach((delta, storeName) => {
          if (delta < 0) origins.push({ storeName, quantity: Math.abs(delta) });
          if (delta > 0) destinations.push({ storeName, quantity: delta });
        });

        destinations.forEach((destination) => {
          let remainingQuantity = destination.quantity;

          origins.forEach((origin, originIndex) => {
            if (remainingQuantity <= 0 || origin.quantity <= 0) return;

            const quantity = Math.min(origin.quantity, remainingQuantity);
            const existingItem = groupedItems.find((item) => (
              item.product_id === product.product_id
              && item.previous_store_name === origin.storeName
              && item.new_store_name === destination.storeName
            ));

            if (existingItem) {
              existingItem.new_quantity += quantity;
            } else {
              groupedItems.push({
                product_id: product.product_id,
                product_name: product.product_name,
                sku: product.sku,
                previous_store_name: origin.storeName,
                new_store_name: destination.storeName,
                new_quantity: quantity,
              });
            }

            origins[originIndex] = {
              ...origin,
              quantity: origin.quantity - quantity,
            };
            remainingQuantity -= quantity;
          });
        });

        return groupedItems;
      }, []);
    },
  },
  methods: {
    formatFullDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
  },
};
</script>

<template>
<v-card outlined class="elevation-1 grey lighten-5 pa-4">
  <div class="d-flex justify-space-between align-start flex-wrap mb-4">

    <div class="d-flex flex-column align-start">
      <h3 class="text-subtitle-1 font-weight-bold grey--text text--darken-4 mb-1">
        Reasignacion de Mercancia
      </h3>

      <div class="text-caption grey--text text--darken-2
        d-flex align-center flex-wrap">
        <v-icon small class="mr-1">mdi-calendar-clock</v-icon>
        {{ formatFullDate(item.created_at) }} hrs.
        <span class="mx-2">&bull;</span>

        <v-icon small class="mr-1">mdi-account-tie</v-icon>
        Entrega: <strong class="ml-1">{{ item.employee_name }}</strong>
      </div>
    </div>
  </div>

  <div class="white pa-3 rounded border mb-2">
    <div class="text-caption font-weight-bold text-uppercase
      grey--text text--darken-1 mb-2">
      Artículos reasignados:
    </div>
    <v-simple-table>
      <thead>
        <tr>
          <th class="text-left text-uppercase font-weight-bold grey--text text--darken-2">
            Producto
          </th>
          <th class="text-center text-uppercase font-weight-bold grey--text text--darken-2">
            Origen
          </th>
          <th class="text-center text-uppercase font-weight-bold grey--text text--darken-2">
            Destino
          </th>
          <th class="text-center text-uppercase font-weight-bold grey--text text--darken-2">
            Cantidad
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="itemDetail in groupedItems"
          :key="`${itemDetail.product_id}-${itemDetail.previous_store_name}-${itemDetail.new_store_name}`"
        >
          <td class="text-left text-body-2 grey--text text--darken-3">
            {{ itemDetail.product_name }}
            <span v-if="itemDetail.sku" class="grey--text text-caption">({{ itemDetail.sku }})</span>
          </td>
          <td class="text-center text-body-2 grey--text text--darken-3">
            {{ itemDetail.previous_store_name }}
          </td>
          <td class="text-center text-body-2 grey--text text--darken-3">
            {{ itemDetail.new_store_name }}
          </td>
          <td class="text-center text-body-2 grey--text text--darken-3">
            {{ itemDetail.new_quantity }}
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <!-- <ul class="pl-4 text-body-2">
      <li v-for="item in item.items" :key="item.product_id" class="mb-1">
        <span class="item-text-red font-weight-bold">
          {{ item.old_quantity }}
        </span> →
        <span class="item-text-green font-weight-bold">
          {{ item.new_quantity }}
        </span> pz
        &times;
        <span>[ {{ item.new_store_name }} ]</span>
        <span> {{ item.product_name }} </span>
        <span v-if="item.sku" class="grey--text text-caption">({{ item.sku }})</span>
      </li>
    </ul> -->
  </div>

  <div
    v-if="item.notes"
    class="bg-slate-50 pa-3 rounded border border-slate-200 text-body-2 mt-3"
  >
    <div class="d-flex align-center mb-1">
      <v-icon small color="grey darken-1" class="mr-2">
        mdi-message-text-outline
      </v-icon>
      <strong class="text-slate-700">Notas de entrega:</strong>
    </div>

    <div class="text-slate-600 font-italic pl-7">
      "{{ item.notes }}"
    </div>
  </div>
</v-card>
</template>

<style>
.item-text-red {
  color: #e53935; /* red */
}
.item-text-green {
  color: #43a047; /* green */
}
</style>
